import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { axiosInstance } from '../utils/axiosConfig';
import '../styles/SubscriptionSettings.css';

interface Plan {
  id: string;
  name: string;
  displayName: string;
  description: string;
  monthlyPrice: number;
  yearlyPrice: number;
  currency: string;
  features: Record<string, boolean>;
  jobBoardsLimit: number;
  searchLimit: number;
  resumeLimit: number;
  aiCredits: number;
  prioritySupport: boolean;
}

interface Subscription {
  id: string;
  status: string;
  billingCycle: string;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  autoRenew: boolean;
  plan: Plan;
}

interface Usage {
  search: { used: number; limit: number };
  ai_resume: { used: number; limit: number };
  ai_interview: { used: number; limit: number };
  resume_upload: { used: number; limit: number };
}

interface Payment {
  id: string;
  amount: number;
  currency: string;
  status: string;
  paidAt: string;
  subscription: { plan: Plan };
}

const SubscriptionSettings: React.FC = () => {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [payments, setPayments] = useState<Payment[]>([]);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      const [plansRes, subRes, usageRes, paymentsRes] = await Promise.allSettled([
        axiosInstance.get('/auth/subscription/plans'),
        axiosInstance.get('/auth/subscription'),
        axiosInstance.get('/auth/subscription/usage'),
        axiosInstance.get('/auth/subscription/payments')
      ]);

      if (plansRes.status === 'fulfilled') setPlans(plansRes.value.data.plans || []);
      if (subRes.status === 'fulfilled') setSubscription(subRes.value.data.subscription || null);
      if (usageRes.status === 'fulfilled') setUsage(usageRes.value.data.usage || null);
      if (paymentsRes.status === 'fulfilled') setPayments(paymentsRes.value.data.payments || []);

      // Self-healing (2026-07-19): if we look like a free user, reconcile once
      // against Stripe — recovers paid checkouts whose activation was missed
      // (undelivered webhook / user never reached the success page).
      const currentPlan = subRes.status === 'fulfilled' ? subRes.value.data.subscription?.plan?.name : null;
      if ((currentPlan === 'free' || !currentPlan) && !sessionStorage.getItem('hb_sub_synced')) {
        sessionStorage.setItem('hb_sub_synced', '1'); // once per session
        try {
          const syncRes = await axiosInstance.post('/subscription/sync', {});
          if (syncRes.data?.synced) {
            const fresh = await axiosInstance.get('/auth/subscription');
            setSubscription(fresh.data.subscription || null);
            setSuccessMsg('Your subscription was restored from Stripe.');
          }
        } catch { /* sync is best-effort */ }
      }
    } catch (err) {
      setError('Failed to load subscription data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpgrade = async (planName: string) => {
    if (subscription?.plan.name === planName) return;

    setUpgrading(true);
    setError(null);
    setSuccessMsg(null);

    try {
      // First get the plan to check if it's free or paid
      const planToUpgrade = plans.find(p => p.name === planName);
      if (!planToUpgrade) {
        throw new Error('Plan not found');
      }

      const price = billingCycle === 'yearly' ? planToUpgrade.yearlyPrice : planToUpgrade.monthlyPrice;
      
      // If it's a free plan, use the upgrade endpoint
      if (price === 0) {
        const response = await axiosInstance.post('/auth/subscription/upgrade', {
          planName,
          billingCycle
        });

        if (response.data.message || response.data.subscription) {
          setSuccessMsg('Subscription updated successfully!');
          fetchData();
        } else {
          throw new Error('Failed to update subscription');
        }
      } else {
        // For paid plans, use the create-checkout endpoint
        const response = await axiosInstance.post('/subscription/create-checkout', {
          planId: planToUpgrade.id,
          billingCycle
        });

        if (response.data.data?.checkoutUrl) {
          // Redirect to Stripe checkout
          window.location.href = response.data.data.checkoutUrl;
        } else {
          throw new Error('Failed to create checkout session');
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to upgrade subscription');
    } finally {
      setUpgrading(false);
    }
  };

  const handleCancel = async () => {
    setError(null);

    try {
      const response = await axiosInstance.post('/auth/subscription/cancel', {
        reason: cancelReason
      });

      if (response.data.success) {
        setShowCancelModal(false);
        setCancelReason('');
        setSuccessMsg('Subscription cancelled. You retain access until the end of your billing period.');
        fetchData();
      } else {
        throw new Error(response.data.error || 'Failed to cancel');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to cancel');
    }
  };

  const formatPrice = (price: number, currency: string): string => {
    const symbols: Record<string, string> = { GBP: '£', USD: '$', EUR: '€' };
    return `${symbols[currency] || currency}${price.toFixed(2)}`;
  };

  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const getFeatureLabel = (key: string): string => {
    const labels: Record<string, string> = {
      basicSearch: 'Basic Job Search',
      savedJobs: 'Save Jobs',
      applicationTracking: 'Application Tracking',
      resumeUpload: 'Resume Upload',
      aiResume: 'AI Resume Optimization',
      aiInterview: 'AI Interview Prep',
      advancedFilters: 'Advanced Search Filters',
      multipleJobBoards: 'Multiple Job Boards',
      prioritySupport: 'Priority Support',
      analytics: 'Analytics Dashboard',
      unlimitedSearch: 'Unlimited Searches',
      unlimitedResumes: 'Unlimited Resumes',
      exclusiveJobs: 'Exclusive Job Listings'
    };
    return labels[key] || key;
  };

  if (loading) {
    return (
      <div className="sub-container">
        <div className="sub-loading">Loading subscription details...</div>
      </div>
    );
  }

  return (
    <div className="sub-container">
      <div className="sub-header">
        <button className="sub-back-btn" onClick={() => navigate('/dashboard')}>
          ← Back to Dashboard
        </button>
        <h1>Subscription & Billing</h1>
        <p>Manage your subscription and view billing history</p>
      </div>

      {error && <div className="sub-error">{error}</div>}
      {successMsg && <div className="sub-success">{successMsg}</div>}

      {/* Current Plan */}
      {subscription && (
        <div className="sub-current">
          <h2>Current Plan</h2>
          <div className="sub-current-card">
            <div className="sub-current-info">
              <span className="sub-plan-name">{subscription.plan.displayName}</span>
              <span className={`sub-status ${subscription.status}`}>
                {subscription.status}
              </span>
            </div>
            <p className="sub-period">
              Billing period: {formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}
            </p>
            {subscription.plan.name !== 'free' && (
              <div className="sub-current-actions">
                <span className="sub-auto-renew">
                  Auto-renew: {subscription.autoRenew ? 'On' : 'Off'}
                </span>
                <button 
                  className="sub-cancel-btn"
                  onClick={() => setShowCancelModal(true)}
                >
                  Cancel Subscription
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Usage */}
      {usage && (
        <div className="sub-usage">
          <h2>Usage This Period</h2>
          <div className="sub-usage-grid">
            <div className="sub-usage-item">
              <span className="sub-usage-label">Searches</span>
              <div className="sub-usage-bar">
                <div 
                  className="sub-usage-fill" 
                  style={{ width: `${Math.min(100, (usage.search.used / usage.search.limit) * 100)}%` }}
                />
              </div>
              <span className="sub-usage-count">{usage.search.used} / {usage.search.limit}</span>
            </div>
            <div className="sub-usage-item">
              <span className="sub-usage-label">AI Resume Credits</span>
              <div className="sub-usage-bar">
                <div 
                  className="sub-usage-fill" 
                  style={{ width: `${Math.min(100, (usage.ai_resume.used / usage.ai_resume.limit) * 100)}%` }}
                />
              </div>
              <span className="sub-usage-count">{usage.ai_resume.used} / {usage.ai_resume.limit}</span>
            </div>
            <div className="sub-usage-item">
              <span className="sub-usage-label">AI Interview Credits</span>
              <div className="sub-usage-bar">
                <div 
                  className="sub-usage-fill" 
                  style={{ width: `${Math.min(100, (usage.ai_interview.used / usage.ai_interview.limit) * 100)}%` }}
                />
              </div>
              <span className="sub-usage-count">{usage.ai_interview.used} / {usage.ai_interview.limit}</span>
            </div>
            <div className="sub-usage-item">
              <span className="sub-usage-label">Resumes</span>
              <div className="sub-usage-bar">
                <div 
                  className="sub-usage-fill" 
                  style={{ width: `${Math.min(100, (usage.resume_upload.used / usage.resume_upload.limit) * 100)}%` }}
                />
              </div>
              <span className="sub-usage-count">{usage.resume_upload.used} / {usage.resume_upload.limit}</span>
            </div>
          </div>
        </div>
      )}

      {/* Plans */}
      <div className="sub-plans">
        <div className="sub-plans-header">
          <h2>Available Plans</h2>
          <div className="sub-billing-toggle">
            <button 
              className={billingCycle === 'monthly' ? 'active' : ''}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button 
              className={billingCycle === 'yearly' ? 'active' : ''}
              onClick={() => setBillingCycle('yearly')}
            >
              Yearly (Save 17%)
            </button>
          </div>
        </div>

        <div className="sub-plans-grid">
          {plans.map(plan => {
            const isCurrentPlan = subscription?.plan.name === plan.name;
            const price = billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
            const period = billingCycle === 'yearly' ? '/year' : '/month';

            return (
              <div 
                key={plan.id} 
                className={`sub-plan-card ${isCurrentPlan ? 'current' : ''} ${plan.name === 'pro' ? 'popular' : ''}`}
              >
                {plan.name === 'pro' && <div className="sub-popular-badge">Most Popular</div>}
                
                <h3 className="sub-plan-title">{plan.displayName}</h3>
                <p className="sub-plan-desc">{plan.description}</p>
                
                <div className="sub-plan-price">
                  <span className="sub-price-amount">{formatPrice(price, plan.currency)}</span>
                  <span className="sub-price-period">{period}</span>
                </div>

                <ul className="sub-plan-features">
                  {Object.entries(plan.features).map(([key, enabled]) => (
                    <li key={key} className={enabled ? 'enabled' : 'disabled'}>
                      <span className="sub-feature-icon">{enabled ? '✓' : '×'}</span>
                      {getFeatureLabel(key)}
                    </li>
                  ))}
                  <li className="sub-limit">
                    <span className="sub-feature-icon">📊</span>
                    {plan.searchLimit} searches/month
                  </li>
                  <li className="sub-limit">
                    <span className="sub-feature-icon">🤖</span>
                    {plan.aiCredits} AI credits/month
                  </li>
                  <li className="sub-limit">
                    <span className="sub-feature-icon">📄</span>
                    Up to {plan.resumeLimit} resumes
                  </li>
                  <li className="sub-limit">
                    <span className="sub-feature-icon">🔗</span>
                    {plan.jobBoardsLimit} job board{plan.jobBoardsLimit > 1 ? 's' : ''}
                  </li>
                </ul>

                <button
                  className={`sub-plan-btn ${isCurrentPlan ? 'current' : plan.name === 'pro' ? 'popular' : ''}`}
                  onClick={() => handleUpgrade(plan.name)}
                  disabled={isCurrentPlan || upgrading}
                >
                  {isCurrentPlan ? 'Current Plan' : upgrading ? 'Processing...' : plan.monthlyPrice === 0 ? 'Downgrade' : 'Upgrade'}
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Payment History */}
      {payments.length > 0 && (
        <div className="sub-payments">
          <h2>Payment History</h2>
          <table className="sub-payments-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {payments.map(payment => (
                <tr key={payment.id}>
                  <td>{payment.paidAt ? formatDate(payment.paidAt) : '-'}</td>
                  <td>{payment.subscription.plan.displayName}</td>
                  <td>{formatPrice(payment.amount, payment.currency)}</td>
                  <td>
                    <span className={`sub-payment-status ${payment.status}`}>
                      {payment.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Cancel Modal */}
      {showCancelModal && (
        <div className="sub-modal-overlay" onClick={() => setShowCancelModal(false)}>
          <div className="sub-modal" onClick={e => e.stopPropagation()}>
            <h3>Cancel Subscription</h3>
            <p>Are you sure you want to cancel? You'll retain access until the end of your billing period.</p>
            <textarea
              placeholder="Please tell us why you're cancelling (optional)"
              value={cancelReason}
              onChange={e => setCancelReason(e.target.value)}
            />
            <div className="sub-modal-actions">
              <button className="sub-modal-cancel" onClick={() => setShowCancelModal(false)}>
                Keep Subscription
              </button>
              <button className="sub-modal-confirm" onClick={handleCancel}>
                Confirm Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionSettings;
